import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronLeft, ChevronRight, Building2, CheckCircle2, Save, Loader2, CreditCard, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUBMIT_DEAL_TRANSLATIONS } from '@/constants/submit-deal.translations';
import { SubmitDealFormData, UploadedFiles, createInitialFormData } from '@/types/submit-deal.types';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { handleSubmitDeal } from '@/services/cim-generation.service';
import { redirectToSellerCheckout } from '@/lib/seller-checkout';
import {
  StepGeneral, StepOperations, StepLicenses, StepAssets, StepRealEstate,
  StepFinancials, StepAddbacks, StepProjections, StepCulture, StepSwot, StepDocuments
} from '@/components/submit-deal';
import { FinancialDocumentAnalyzer } from './FinancialDocumentAnalyzer';
import { uploadAnalyzedDocuments, getOrCreateDraftSessionId } from '@/lib/upload-deal-documents';

const TOTAL_STEPS = 11;

// AJOUTÉ — génère un token de brouillon. Même pattern que celui déjà
// utilisé dans handleSendForApproval() ci-dessous — factorisé ici pour
// être réutilisable par le nouveau système de brouillon broker.
function generateDraftToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface SubmitDealFormProps {
  onComplete?: () => void;
  // AJOUTÉ — mode formulaire public partagé par un broker (voir
  // DealFormPublic.tsx). Quand publicDraftToken est présent :
  //  - l'étape 11 (upload de documents) est masquée, TOTAL_STEPS = 10
  //  - la sauvegarde se fait vers dealFormDrafts/{token} (Firestore),
  //    pas vers localStorage/DealCheckout
  //  - le bouton final devient "Soumettre à mon courtier", pas
  //    "Finaliser la soumission" (aucun paiement à cette étape)
  publicDraftToken?: string;
  publicDraftMeta?: { brokerId: string; repId: string };
  // AJOUTÉ — vrai quand ce formulaire est rendu depuis BrokerDashboard.tsx.
  // Un broker peut avoir plusieurs deals en cours (un par vendeur
  // représenté) : le brouillon n'est donc JAMAIS lu depuis le localStorage
  // global 'dealSubmissionDraft' (partagé entre tous les deals, cause du
  // bug où "Nouvelle Annonce" réaffichait le dernier deal soumis) — il vit
  // dans Firestore, sur representedSellers/{id}.brokerDraftToken, un par
  // vendeur. Sans resumeSellerId, le formulaire démarre toujours vierge.
  brokerMode?: boolean;
  resumeSellerId?: string;
  // AJOUTÉ — identifie précisément QUELLE entrée de
  // representedSellers/{id}/dealForms/{formId} reprendre. Un vendeur
  // pouvant désormais avoir plusieurs formulaires/compagnies en cours en
  // parallèle, resumeSellerId seul ne suffit plus à savoir lequel charger.
  resumeFormId?: string;
}

export const SubmitDealForm = ({ onComplete, publicDraftToken, publicDraftMeta, brokerMode, resumeSellerId, resumeFormId }: SubmitDealFormProps) => {
  const isPublicMode = !!publicDraftToken;
  // TOTAL_STEPS devient une valeur dérivée plutôt qu'une constante fixe
  // — 10 en mode public (pas d'étape 11 upload), 11 sinon.
  const totalSteps = isPublicMode ? 10 : TOTAL_STEPS;
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = SUBMIT_DEAL_TRANSLATIONS[language as 'en' | 'fr'] || SUBMIT_DEAL_TRANSLATIONS.en;

  const [formData, setFormData] = useState<SubmitDealFormData>(createInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({
    documents: [], photos: [], logo: null
  });
  const [showAnalyzer, setShowAnalyzer] = useState(!publicDraftToken);
  const [analyzedFiles, setAnalyzedFiles] = useState<{ name: string; base64: string; mimeType: string }[]>([]);
const [analyzedDocs, setAnalyzedDocs] = useState<{ name: string; mimeType: string; url: string; path: string }[]>([]);

  // CORRIGÉ — ne restaure plus depuis localStorage en mode broker
  // (brokerMode) : ce chemin est désormais entièrement remplacé par le
  // système de brouillon Firestore par vendeur (effet dédié plus bas).
  // C'était cette restauration inconditionnelle qui rechargeait le
  // dernier deal soumis (n'importe lequel, peu importe le vendeur) à
  // chaque ouverture de "Nouvelle Annonce" — bug rapporté. Reste
  // inchangé pour le vendeur qui soumet lui-même son propre deal
  // (aucun contexte broker/multi-deal à ce niveau, un seul brouillon
  // possible à la fois).
  useEffect(() => {
    if (isPublicMode || brokerMode) return;
    const saved = localStorage.getItem('dealSubmissionDraft');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.formData) {
          setFormData(parsed.formData);
          setShowAnalyzer(false); // l'utilisateur a déjà dépassé l'analyseur de documents
        }
        if (parsed.uploadedFiles) setUploadedFiles(parsed.uploadedFiles);
        if (parsed.analyzedDocs) setAnalyzedDocs(parsed.analyzedDocs);
      } catch (e) {
        console.error('[SubmitDealForm] Failed to restore draft', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AJOUTÉ — token de l'entrée representedSellers/{id}/dealForms/{token}
  // actuellement éditée en mode broker. null tant qu'aucune sauvegarde
  // n'a encore créé d'entrée pour ce brouillon précis ; fixé au montage
  // si on reprend une entrée existante (resumeFormId), ou au moment du
  // premier saveDraft()/handleSendForApproval() réussi sinon.
  const [brokerFormId, setBrokerFormId] = useState<string | null>(resumeFormId || null);

  // AJOUTÉ — mode broker : charge (ou réinitialise) le formulaire selon
  // resumeSellerId + resumeFormId. C'est le mécanisme qui remplace le
  // localStorage global pour ce cas : chaque COMPAGNIE d'un vendeur
  // représenté a sa propre entrée dans representedSellers/{id}/dealForms,
  // référencée précisément par resumeFormId — plusieurs peuvent coexister
  // pour un même vendeur.
  useEffect(() => {
    if (!brokerMode) return;

    if (!resumeSellerId) {
      // "Nouvelle Annonce" explicite, sans vendeur ni formulaire précis à
      // reprendre — repartir strictement à zéro.
      setFormData(createInitialFormData());
      setUploadedFiles({ documents: [], photos: [], logo: null });
      setAnalyzedDocs([]);
      setShowAnalyzer(true);
      setBrokerFormId(null);
      return;
    }

    if (!resumeFormId) {
      // Vendeur choisi, mais aucun formulaire précis à reprendre — cas du
      // tout premier remplissage pour ce vendeur (l'entrée dealForms sera
      // créée au premier saveDraft() ou handleSendForApproval()).
      (async () => {
        try {
          const sellerRef = doc(db, 'representedSellers', resumeSellerId);
          const sellerSnap = await getDoc(sellerRef);
          const sellerData: any = sellerSnap.exists() ? sellerSnap.data() : {};
          const initial = createInitialFormData();
          setFormData({
            ...initial,
            general: {
              ...initial.general,
              brokerId: user?.id,
              representedSellerId: resumeSellerId,
              representedSellerName: sellerData.name || '',
              representedSellerEmail: sellerData.email || '',
            } as any,
          });
          setUploadedFiles({ documents: [], photos: [], logo: null });
          setAnalyzedDocs([]);
          setShowAnalyzer(true);
          setBrokerFormId(null);
        } catch (e) {
          console.error('[SubmitDealForm] Failed to load seller for new form', e);
        }
      })();
      return;
    }

    // resumeSellerId ET resumeFormId présents — reprise d'une entrée
    // précise dans representedSellers/{resumeSellerId}/dealForms/{resumeFormId}.
    (async () => {
      try {
        const draftSnap = await getDoc(doc(db, 'dealFormDrafts', resumeFormId));
        if (draftSnap.exists()) {
          const draftData: any = draftSnap.data();
          if (draftData.formData) {
            // Contrairement au flux vendeur (currentStep forcé à 1 dans
            // l'effet public plus bas), on reprend ici EXACTEMENT où le
            // broker s'était arrêté — c'est son propre brouillon.
            setFormData(draftData.formData);
            setShowAnalyzer(false);
          }
          if (draftData.uploadedFiles) setUploadedFiles(draftData.uploadedFiles);
          if (draftData.analyzedDocs) setAnalyzedDocs(draftData.analyzedDocs);
        }
        setBrokerFormId(resumeFormId);
      } catch (e) {
        console.error('[SubmitDealForm] Failed to load broker draft entry', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerMode, resumeSellerId, resumeFormId]);

  // AJOUTÉ — en mode public, charge le formulaire existant depuis
  // dealFormDrafts/{token} (Firestore) plutôt que localStorage — un
  // vendeur qui revient sur son lien plus tard retrouve ce qu'il avait
  // déjà rempli. Pré-remplit aussi representedSellerId/brokerId depuis
  // publicDraftMeta, puisque ces champs sont déjà connus via le token
  // (pas besoin que le vendeur les sélectionne lui-même).
  useEffect(() => {
    if (!isPublicMode || !publicDraftToken) return;
    (async () => {
      try {
        const ref = doc(db, 'dealFormDrafts', publicDraftToken);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
                    if (data.formData) {
            // Un brouillon existe déjà (le vendeur revient sur son lien
            // après un premier passage, OU le broker a pré-rempli via
            // "Envoyer pour approbation") — on saute l'analyseur puisqu'il
            // a déjà dépassé cette étape la première fois. currentStep est
            // FORCÉ à 1 : ce champ fait partie de formData et reflète où
            // le BROKER s'était arrêté en remplissant (potentiellement
            // l'étape 10/11) — sans ce correctif, le vendeur atterrissait
            // directement à la dernière étape du broker au lieu du début
            // du formulaire, avec en prime un affichage incohérent
            // "Step 11 of 10".
            setFormData({ ...data.formData, currentStep: 1 });
            setShowAnalyzer(false);
          } else if (publicDraftMeta) {

            // Premier chargement, aucune donnée encore sauvegardée —
            // pré-remplir brokerId/representedSellerId depuis le token,
            // et LAISSER l'analyseur s'afficher normalement (showAnalyzer
            // reste à true, sa valeur initiale) pour que le vendeur
            // puisse uploader ses documents dès le départ.
            updateFormData('general', {
              brokerId: publicDraftMeta.brokerId,
              representedSellerId: publicDraftMeta.repId,
            });
          }
        }
      } catch (e) {
        console.error('[SubmitDealForm] Failed to load public draft', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicMode, publicDraftToken]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentCompleted(true);
      setShowAnalyzer(false);
      toast({
        title: language === 'fr' ? 'Paiement réussi!' : 'Payment successful!',
        description: language === 'fr'
          ? 'Vous pouvez maintenant soumettre votre deal.'
          : 'You can now submit your deal.',
      });
      setSearchParams({});
    } else if (payment === 'canceled') {
      toast({
        title: language === 'fr' ? 'Paiement annulé' : 'Payment canceled',
        description: language === 'fr'
          ? 'Vous pouvez réessayer quand vous êtes prêt.'
          : 'You can try again when ready.',
        variant: 'destructive',
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, toast, language]);

  const updateFormData = useCallback(<K extends keyof SubmitDealFormData>(
    section: K, updates: Partial<SubmitDealFormData[K]>
  ) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section] as object, ...updates }
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  }, []);

  const applyExtractedData = (data: any, uploadedDocs: { name: string; mimeType: string; url: string; path: string }[] = []) => {
  setAnalyzedDocs(uploadedDocs);
  // General
  if (data.legalName)         updateFormData('general', { legalName: data.legalName });
  if (data.tradeName)         updateFormData('general', { tradeName: data.tradeName });
  if (data.yearEstablished)   updateFormData('general', { yearEstablished: data.yearEstablished });
  if (data.entityType)        updateFormData('general', { entityType: data.entityType });
  if (data.jurisdiction)      updateFormData('general', { locationCountry: data.jurisdiction });
  if (data.naicsCode)         updateFormData('general', { naicsCode: data.naicsCode });
  if (data.website)           updateFormData('general', { website: data.website });
  if (data.reasonForSale)     updateFormData('general', { reasonForSale: data.reasonForSale });
  // Operations
  if (data.employees)         updateFormData('operations', { fullTimeEmployees: data.employees });
  if (data.partTimeEmployees) updateFormData('operations', { partTimeEmployees: data.partTimeEmployees });
  if (data.ownerSalary)       updateFormData('operations', { ownerAnnualSalary: data.ownerSalary });
  if (data.ownerHoursPerWeek) updateFormData('operations', { ownerHoursPerWeek: data.ownerHoursPerWeek });
  if (data.customerConcentration) updateFormData('operations', { customerConcentration: data.customerConcentration });
  if (data.keySuppliers)      updateFormData('operations', { keySuppliers: data.keySuppliers });
  // Financials
  if (data.grossRevenue?.year1) updateFormData('financials', { revenueYear1: data.grossRevenue.year1 });
  if (data.grossRevenue?.year2) updateFormData('financials', { revenueYear2: data.grossRevenue.year2 });
  if (data.grossRevenue?.year3) updateFormData('financials', { revenueYear3: data.grossRevenue.year3 });
  if (data.ebitda?.year1)     updateFormData('financials', { ebitdaYear1: data.ebitda.year1 });
  if (data.ebitda?.year2)     updateFormData('financials', { ebitdaYear2: data.ebitda.year2 });
  if (data.ebitda?.year3)     updateFormData('financials', { ebitdaYear3: data.ebitda.year3 });
  if (data.sde)               updateFormData('financials', { cashFlowEquity: data.sde });
  if (data.askingPriceSuggested) updateFormData('financials', { askingPrice: data.askingPriceSuggested });
  if (data.totalLiabilities)  updateFormData('financials', { totalLiabilities: data.totalLiabilities });
  if (data.inheritedDebt)     updateFormData('financials', { inheritedDebt: data.inheritedDebt });
  // Assets
  if (data.inventory)         updateFormData('assets', { inventoryValue: data.inventory });
  if (data.ffe)                updateFormData('assets', { ffeValue: data.ffe });
  if (data.accountsReceivable) updateFormData('assets', { accountsReceivable: data.accountsReceivable });
  if (data.totalAssets)        updateFormData('assets', { totalAssets: data.totalAssets });

  if (Array.isArray(data.inventoryItems) && data.inventoryItems.length > 0) {
    const existingItems = formData.assets.inventoryItems || [];
    const newItems = data.inventoryItems.map((item: any, i: number) => ({
      id: `ai_inv_${Date.now()}_${i}`,
      description: item.description || '',
      category: item.category || '',
      quantity: item.quantity || 1,
      unitValue: item.unitValue || 0,
    }));
    updateFormData('assets', { inventoryItems: [...existingItems, ...newItems] });
  }

  if (data.realEstate)        updateFormData('realEstate', { realEstateAskPrice: data.realEstate });

  if (Array.isArray(data.realEstateProperties) && data.realEstateProperties.length > 0) {
    const existingProps = formData.realEstate.properties || [];
    const newProps = data.realEstateProperties.map((prop: any, i: number) => ({
      id: `ai_re_${Date.now()}_${i}`,
      address: prop.address || '',
      estimatedValue: prop.estimatedValue || 0,
      status: prop.status === 'leased' ? 'leased' : 'owned',
      monthlyRent: prop.monthlyRent || 0,
      leaseExpirationDate: prop.leaseExpirationDate || '',
      remainingLeaseMonths: prop.remainingLeaseMonths || 0,
      renewalTerms: '',
      assignmentRight: false,
    }));
    updateFormData('realEstate', { properties: [...existingProps, ...newProps] });
    if (newProps.some((p: any) => p.status === 'owned')) {
      updateFormData('realEstate', { isIncluded: true });
    }
  }

  if (data.projections?.year1Revenue) updateFormData('projections', { revenueYear1: data.projections.year1Revenue });
  if (data.projections?.year2Revenue) updateFormData('projections', { revenueYear2: data.projections.year2Revenue });
  if (data.projections?.year3Revenue) updateFormData('projections', { revenueYear3: data.projections.year3Revenue });
  if (data.projections?.year1Ebitda)  updateFormData('projections', { ebitdaYear1: data.projections.year1Ebitda });
  if (data.projections?.year2Ebitda)  updateFormData('projections', { ebitdaYear2: data.projections.year2Ebitda });
  if (data.projections?.year3Ebitda)  updateFormData('projections', { ebitdaYear3: data.projections.year3Ebitda });

  if (data.addbacks?.length > 0) updateFormData('addbacks', { items: data.addbacks });

  if (data.licensing) {
    const lic = data.licensing;
    updateFormData('licensing', {
      ...(typeof lic.specialLicensesRequired === 'boolean' ? { specialLicensesRequired: lic.specialLicensesRequired } : {}),
      ...(lic.licenseDetails ? { licenseDetails: lic.licenseDetails } : {}),
      ...(lic.regulatoryRequirements ? { regulatoryRequirements: lic.regulatoryRequirements } : {}),
      ...(lic.legalDisputes ? { legalDisputes: lic.legalDisputes } : {}),
    });
  }

  if (data.culture) {
    const cult = data.culture;
    const cultureUpdates: any = {};
    if (Array.isArray(cult.coreValues) && cult.coreValues.length > 0) {
      cultureUpdates.coreValues = cult.coreValues;
      cultureUpdates.coreValueDescriptions = cult.coreValues.map(() => '');
    }
    if (typeof cult.managementTeamInPlace === 'boolean') {
      cultureUpdates.managementTeam = cult.managementTeamInPlace ? 'yes' : 'no';
    }
    if (cult.managementTeamExplanation) cultureUpdates.managementTeamExplanation = cult.managementTeamExplanation;
    if (cult.nonCompeteRadius)          cultureUpdates.nonCompeteRadius = cult.nonCompeteRadius;
    if (cult.nonCompeteRadiusUnit)      cultureUpdates.nonCompeteRadiusUnit = cult.nonCompeteRadiusUnit;
    if (Object.keys(cultureUpdates).length > 0) updateFormData('culture', cultureUpdates);

    if (cult.sellerFinancingOffered) {
      const financingUpdates: any = { sellerFinancingOffered: true };
      if (cult.sellerFinancingAmount)      financingUpdates.sellerFinancingAmount = cult.sellerFinancingAmount;
      if (cult.sellerFinancingInterestRate) financingUpdates.sellerFinancingInterestRate = cult.sellerFinancingInterestRate;
      if (cult.sellerFinancingTermMonths)   financingUpdates.sellerFinancingTermMonths = cult.sellerFinancingTermMonths;
      updateFormData('projections', financingUpdates);
    }
  }

  if (data.swot) {
    const swotUpdates: any = {};
    if (data.swot.strengths)     swotUpdates.strengths = data.swot.strengths;
    if (data.swot.weaknesses)    swotUpdates.weaknesses = data.swot.weaknesses;
    if (data.swot.opportunities) swotUpdates.opportunities = data.swot.opportunities;
    if (data.swot.threats)       swotUpdates.threats = data.swot.threats;
    if (Object.keys(swotUpdates).length > 0) updateFormData('swot', swotUpdates);
  }

  if (data.impact) {
    const imp = data.impact;
    const impactUpdates: any = {};
    if (imp.employeeCount)              impactUpdates.employeeCount = imp.employeeCount;
    if (imp.averageEmployeeTenureYears) impactUpdates.averageEmployeeTenureYears = imp.averageEmployeeTenureYears;
    if (typeof imp.hasRetirementPlan === 'boolean')              impactUpdates.hasRetirementPlan = imp.hasRetirementPlan;
    if (imp.workplaceAccidentsLast3yr !== null && imp.workplaceAccidentsLast3yr !== undefined) impactUpdates.workplaceAccidentsLast3yr = imp.workplaceAccidentsLast3yr;
    if (imp.localSupplyChainPercent)    impactUpdates.localSupplyChainPercent = imp.localSupplyChainPercent;
    if (typeof imp.hasEnvironmentalCertification === 'boolean') impactUpdates.hasEnvironmentalCertification = imp.hasEnvironmentalCertification;
    if (typeof imp.hasLitigationHistory === 'boolean')          impactUpdates.hasLitigationHistory = imp.hasLitigationHistory;
    if (typeof imp.communityInvolvement === 'boolean')          impactUpdates.communityInvolvement = imp.communityInvolvement;
    if (Object.keys(impactUpdates).length > 0) updateFormData('impact', impactUpdates);
  }

  setShowAnalyzer(false);
};

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    if (step === 1) {
      if (!formData.general.legalName || formData.general.legalName.length < 2) newErrors.legalName = t.validation.legalNameMinLength;
      if (!formData.general.entityType) newErrors.entityType = t.validation.entityTypeRequired;
      if (!formData.general.title || formData.general.title.length < 3) newErrors.title = t.validation.titleMinLength;
      if (!formData.general.description || formData.general.description.length < 50) newErrors.description = t.validation.descriptionMinLength;
      if (!formData.general.industry) newErrors.industry = t.validation.industryRequired;
      if (!formData.general.locationCity) newErrors.locationCity = t.validation.cityRequired;
      if (!formData.general.locationCountry) newErrors.locationCountry = t.validation.countryRequired;
      if (currentYear - formData.general.yearEstablished < 3) newErrors.yearEstablished = t.validation.businessTooYoung;
      // Un broker doit obligatoirement lier ce deal à un vendeur représenté
      // (StepGeneral.tsx affiche déjà le dropdown si brokerId est présent).
      // Sans cette validation, un broker peut soumettre un deal sans lien
      // vers un vendeur — le deal reste alors invisible pour tout vendeur.
      if (formData.general.brokerId && !formData.general.representedSellerId) {
        newErrors.representedSellerId = language === 'fr'
          ? 'Veuillez sélectionner un vendeur représenté avant de continuer.'
          : 'Please select a represented seller before continuing.';
      }
    } else if (step === 2) {
      if (formData.operations.ownerHoursPerWeek < 0 || formData.operations.ownerHoursPerWeek > 168) newErrors.ownerHoursPerWeek = t.validation.ownerHoursInvalid;
    } else if (step === 6) {
      if (formData.financials.askingPrice < 1000) newErrors.askingPrice = t.validation.askingPriceMinimum;
      if (formData.financials.cashFlowEquity <= 0) newErrors.cashFlowEquity = t.validation.cashFlowPositive;
    } else if (step === 10) {
      if (formData.swot.strengths.length < 10) newErrors.strengths = t.validation.swotMinLength.replace('{field}', t.step10.strengths);
      if (formData.swot.weaknesses.length < 10) newErrors.weaknesses = t.validation.swotMinLength.replace('{field}', t.step10.weaknesses);
      if (formData.swot.opportunities.length < 10) newErrors.opportunities = t.validation.swotMinLength.replace('{field}', t.step10.opportunities);
      if (formData.swot.threats.length < 10) newErrors.threats = t.validation.swotMinLength.replace('{field}', t.step10.threats);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(formData.currentStep)) {
      setFormData(prev => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, totalSteps) }));
    }
  };

  const prevStep = () => {
    setFormData(prev => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, 1) }));
  };

  const [uploadingType, setUploadingType] = useState<'documents' | 'photos' | 'logo' | null>(null);

const handleFileUpload = async (type: 'documents' | 'photos' | 'logo', files: FileList) => {
  setUploadingType(type);
  try {
    const sessionId = getOrCreateDraftSessionId();
    const fileList = Array.from(files);

    // Lire chaque fichier en base64 pour l'upload
    const base64Files = await Promise.all(
      fileList.map(f => new Promise<{ name: string; base64: string; mimeType: string; size: number }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1]; // retire le préfixe "data:mime;base64,"
          resolve({ name: f.name, base64, mimeType: f.type, size: f.size });
        };
        reader.onerror = reject;
        reader.readAsDataURL(f);
      }))
    );

    const uploaded = await uploadAnalyzedDocuments(
      sessionId,
      base64Files.map(({ name, base64, mimeType }) => ({ name, base64, mimeType }))
    );

    // Combine les métadonnées Storage (url, path) avec la taille/type d'origine
    const fileArray = uploaded.map((doc, i) => ({
      name: doc.name,
      size: base64Files[i].size,
      type: doc.mimeType,
      url: doc.url,
      path: doc.path,
    }));

    setUploadedFiles(prev =>
      type === 'logo'
        ? { ...prev, logo: fileArray[0] || null }
        : { ...prev, [type]: [...prev[type], ...fileArray] }
    );
  } catch (err) {
    console.error(`[SubmitDealForm] Failed to upload ${type}:`, err);
    toast({
      title: language === 'fr' ? 'Erreur de téléversement' : 'Upload error',
      description: language === 'fr'
        ? "Un ou plusieurs fichiers n'ont pas pu être téléversés. Réessayez."
        : 'One or more files failed to upload. Please try again.',
      variant: 'destructive',
    });
  } finally {
    setUploadingType(null);
  }
};

  const handleFileRemove = (type: 'documents' | 'photos' | 'logo', index?: number) => {
    setUploadedFiles(prev => type === 'logo' ? { ...prev, logo: null } : { ...prev, [type]: prev[type].filter((_, i) => i !== index) });
  };

  // CORRIGÉ — trois chemins distincts maintenant : mode public (inchangé),
  // mode broker (Firestore, un brouillon par vendeur représenté — AJOUTÉ),
  // et vendeur soumettant lui-même (localStorage, inchangé).
  const saveDraft = async () => {
    if (isPublicMode && publicDraftToken) {
      // Mode public : sauvegarde vers dealFormDrafts/{token}, jamais
      // dans localStorage (le vendeur peut revenir depuis un autre
      // appareil avec le même lien — localStorage ne suivrait pas).
      try {
        await setDoc(doc(db, 'dealFormDrafts', publicDraftToken), {
          formData,
          status: 'in_progress',
          updatedAt: serverTimestamp(),
        }, { merge: true });
        toast({ title: t.messages.draftSaved, description: t.messages.draftSavedDescription });
      } catch (e) {
        console.error('[SubmitDealForm] Failed to save public draft', e);
        toast({
          title: language === 'fr' ? 'Erreur' : 'Error',
          description: language === 'fr' ? 'La sauvegarde a échoué. Réessayez.' : 'Save failed. Please try again.',
          variant: 'destructive',
        });
      }
      return;
    }

    if (brokerMode) {
      const sellerId = formData.general.representedSellerId;
      if (!sellerId) {
        toast({
          title: language === 'fr' ? 'Vendeur requis' : 'Seller required',
          description: language === 'fr'
            ? "Sélectionnez un vendeur représenté à l'étape 1 avant d'enregistrer ce brouillon."
            : 'Select a represented seller in step 1 before saving this draft.',
          variant: 'destructive',
        });
        return;
      }
      try {
        // AJOUTÉ — token stable de CETTE session d'édition : réutilise
        // brokerFormId s'il existe déjà (entrée dealForms déjà créée,
        // reprise ou issue d'un premier saveDraft() précédent), sinon en
        // génère un nouveau — ce sera alors la toute première sauvegarde
        // de cette compagnie précise pour ce vendeur.
        const isNewEntry = !brokerFormId;
        const token = brokerFormId || generateDraftToken();

        const draftPayload: any = {
          token,
          authorRole: 'broker',
          brokerId: user?.id || null,
          repId: sellerId,
          formData,
          uploadedFiles,
          analyzedDocs,
          status: 'in_progress',
          revoked: false,
          updatedAt: serverTimestamp(),
        };
        if (isNewEntry) draftPayload.createdAt = serverTimestamp();

        await setDoc(doc(db, 'dealFormDrafts', token), draftPayload, { merge: true });

        if (isNewEntry) {
          await setDoc(doc(db, 'representedSellers', sellerId, 'dealForms', token), {
            token,
            origin: 'broker',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          setBrokerFormId(token);
        } else {
          await updateDoc(doc(db, 'representedSellers', sellerId, 'dealForms', token), {
            updatedAt: serverTimestamp(),
          });
        }

        toast({ title: t.messages.draftSaved, description: t.messages.draftSavedDescription });
      } catch (e) {
        console.error('[SubmitDealForm] Failed to save broker draft', e);
        toast({
          title: language === 'fr' ? 'Erreur' : 'Error',
          description: language === 'fr' ? 'La sauvegarde a échoué. Réessayez.' : 'Save failed. Please try again.',
          variant: 'destructive',
        });
      }
      return;
    }

    // Vendeur soumettant lui-même son propre deal — pas de contexte
    // broker/multi-deal à ce niveau, localStorage suffit.
    localStorage.setItem('dealSubmissionDraft', JSON.stringify({ formData, uploadedFiles, analyzedDocs }));
    toast({ title: t.messages.draftSaved, description: t.messages.draftSavedDescription });
  };

  const handleSellerPayment = async () => {
    if (!user) {
      toast({ title: t.messages.authRequired, variant: 'destructive' });
      return;
    }
    try {
      setPaymentProcessing(true);
      localStorage.setItem('dealSubmissionPending', JSON.stringify({ formData, uploadedFiles, analyzedDocs }));
      await redirectToSellerCheckout({
        paymentStep: 'pre_evaluation',
        userType: 'seller',
        dealId: 'pending',
        email: user.email || '',
      });
    } catch (error: any) {
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: error.message || (language === 'fr'
          ? 'Une erreur est survenue lors de la création du paiement.'
          : 'An error occurred while creating the payment.'),
        variant: 'destructive',
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async () => {
  if (!user) { toast({ title: t.messages.authRequired, variant: 'destructive' }); return; }
  if (!validateStep(formData.currentStep)) return;
 
  // AJOUTÉ — brokerFormId inclus pour que PaymentSuccess.tsx puisse
  // marquer la bonne entrée representedSellers/{id}/dealForms/{token}
  // comme finalisée une fois le deal réellement créé.
  localStorage.setItem('dealSubmissionDraft', JSON.stringify({ formData, uploadedFiles, analyzedDocs, brokerFormId }));
 
  // Naviguer vers la page de checkout dédiée
  navigate('/seller/deal-checkout');
};

  // AJOUTÉ — soumission finale en mode public (pas de compte, pas de
  // paiement à cette étape). Écrit le formulaire complété dans
  // dealFormDrafts/{token} avec status: 'submitted' — plus aucune
  // écriture n'est possible sur ce document après ça (voir la règle
  // Firestore dédiée). Le broker est notifié séparément (Cloud
  // Function, câblée ailleurs) au changement de statut.
  const [publicSubmitted, setPublicSubmitted] = useState(false);
  const [publicSubmitting, setPublicSubmitting] = useState(false);
      const handleSubmitToBroker = async () => {
    if (!publicDraftToken) return;
    if (!validateStep(formData.currentStep)) return;
    setPublicSubmitting(true);
    try {
      await setDoc(doc(db, 'dealFormDrafts', publicDraftToken), {
        formData,
        status: 'submitted',
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      if (publicDraftMeta?.repId) {
        try {
          await updateDoc(doc(db, 'representedSellers', publicDraftMeta.repId), {
            formStatus: 'submitted',
            formSubmittedAt: serverTimestamp(),
          });
        } catch (syncErr) {
          console.warn('[SubmitDealForm] Failed to sync formStatus to representedSellers', syncErr);
        }
      }

      setPublicSubmitted(true);
    } catch (e) {
      console.error('[SubmitDealForm] Failed to submit public draft', e);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: language === 'fr'
          ? "La soumission a échoué. Vérifiez votre connexion et réessayez."
          : 'Submission failed. Check your connection and try again.',
        variant: 'destructive',
      });
    } finally {
      setPublicSubmitting(false);
    }
  };

  // AJOUTÉ — chemin (b) : le broker remplit lui-même le formulaire (avec
  // son propre analyseur, déjà authentifié et fonctionnel), puis envoie
  // ce brouillon complété au vendeur représenté pour révision et
  // approbation, via le même mécanisme dealFormDrafts que le chemin (a).
  // Réutilise/complète le token existant sur le vendeur (formToken) s'il
  // y en a déjà un non soumis, sinon en crée un nouveau — même logique
  // que handleShareForm() dans BrokerDashboard.tsx, dupliquée ici car
  // ce composant n'a pas accès direct à representedSellers (chargé côté
  // dashboard, pas ici).
  const [sendingForApproval, setSendingForApproval] = useState(false);
  // AJOUTÉ — dialogue de lien copiable après envoi, même besoin que dans
  // BrokerDashboard.tsx (handleShareForm) : l'email de notification au
  // vendeur n'est pas câblé, donc le broker doit pouvoir récupérer et
  // partager le lien lui-même. Avant ce correctif, un simple toast puis
  // onComplete?.() redirigeait le broker sans rien lui laisser à copier.
  const [approvalLinkUrl, setApprovalLinkUrl] = useState<string | null>(null);
  const [approvalLinkCopied, setApprovalLinkCopied] = useState(false);
    const handleCopyApprovalLink = async () => {
    if (!approvalLinkUrl) return;
    let succeeded = false;
    try {
      await navigator.clipboard.writeText(approvalLinkUrl);
      succeeded = true;
    } catch {
      // Échec silencieux de l'API clipboard (fréquent en contexte non
      // sécurisé, ex. IP locale en HTTP) — on ne prétend jamais avoir
      // réussi. Sélectionne le texte pour un Ctrl+C manuel à la place.
      const input = document.getElementById('approval-link-input') as HTMLInputElement | null;
      input?.select();
    }
    if (succeeded) {
      setApprovalLinkCopied(true);
      setTimeout(() => setApprovalLinkCopied(false), 2000);
    } else {
      toast({
        title: language === 'fr' ? 'Copie automatique indisponible' : 'Automatic copy unavailable',
        description: language === 'fr'
          ? 'Le texte est sélectionné — utilisez Ctrl+C (ou Cmd+C) pour le copier manuellement.'
          : 'The text is selected — use Ctrl+C (or Cmd+C) to copy it manually.',
      });
    }
  };
  const handleSendForApproval = async () => {
    if (!user || isPublicMode) return;
    if (!formData.general.representedSellerId) {
      toast({
        title: language === 'fr' ? 'Vendeur requis' : 'Seller required',
        description: language === 'fr'
          ? "Sélectionnez un vendeur représenté à l'étape 1 avant d'envoyer pour approbation."
          : 'Select a represented seller in step 1 before sending for approval.',
        variant: 'destructive',
      });
      return;
    }
    setSendingForApproval(true);
    try {
      const sellerId = formData.general.representedSellerId;
      // AJOUTÉ — réutilise le token de CETTE session (brokerFormId) s'il
      // existe déjà, pour que le vendeur reprenne exactement où le broker
      // s'est arrêté sur la MÊME entrée, plutôt que d'en générer une
      // nouvelle. S'il n'y en avait pas encore (le broker envoie sans
      // jamais avoir cliqué "Enregistrer le brouillon" avant), on en crée
      // une ici pour la première fois.
      const isNewEntry = !brokerFormId;
      const token = brokerFormId || generateDraftToken();

      const draftPayload: any = {
        token,
        brokerId: user.id,
        repId: sellerId,
        sellerName: formData.general.representedSellerName || '',
        sellerEmail: formData.general.representedSellerEmail || '',
        status: 'in_progress', // le vendeur reprend l'édition, pas encore soumis
        formData, // pré-rempli par le broker — c'est la différence clé avec le chemin (a)
        uploadedFiles,
        analyzedDocs,
        revoked: false,
        updatedAt: serverTimestamp(),
        submittedAt: null,
      };
      if (isNewEntry) draftPayload.createdAt = serverTimestamp();

      await setDoc(doc(db, 'dealFormDrafts', token), draftPayload, { merge: true });

      if (isNewEntry) {
        await setDoc(doc(db, 'representedSellers', sellerId, 'dealForms', token), {
          token,
          origin: 'shared',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        // AJOUTÉ — bascule cette entrée de "brouillon broker" à "partagé
        // avec le vendeur" : même token, le vendeur reprend exactement là
        // où le broker s'est arrêté, sans nouvelle entrée dupliquée.
        await updateDoc(doc(db, 'representedSellers', sellerId, 'dealForms', token), {
          origin: 'shared',
          updatedAt: serverTimestamp(),
        });
      }

      const url = `${window.location.origin}/deal-form/${token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignoré, voir dialogue */ }
      setBrokerFormId(token);
      setApprovalLinkUrl(url);
    } catch (e) {
      console.error('[SubmitDealForm] Failed to send for approval', e);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: language === 'fr' ? "L'envoi a échoué. Réessayez." : 'Send failed. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingForApproval(false);
    }
  };

  // AJOUTÉ — isPublicMode transmis explicitement à StepGeneral, pour
  // qu'il coupe son sélecteur "vendeur représenté" (voir StepGeneral.tsx)
  // en mode public. Les autres étapes reçoivent aussi ce champ mais
  // l'ignorent simplement, sans impact.
  const stepProps = { formData, updateFormData, errors, clearError, t, isPublicMode };
  const stepTitles = Object.values(t.steps).slice(0, totalSteps);

  // Show analyzer before form
  // AJOUTÉ — écran de confirmation en mode public, remplace tout le
  // formulaire une fois la soumission au courtier réussie.
  if (isPublicMode && publicSubmitted) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: '#CEAB65' }} />
        <h1 className="text-2xl font-semibold text-[#1D314D]">
          {language === 'fr' ? 'Formulaire complété !' : 'Form completed!'}
        </h1>
        <p className="text-muted-foreground">
          {language === 'fr'
            ? 'Votre courtier a été notifié et vous contactera pour les documents justificatifs à fournir.'
            : 'Your broker has been notified and will contact you for the supporting documents to provide.'}
        </p>
      </div>
    );
  }

  if (showAnalyzer) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t.pageTitle}</h1>
            <p className="text-muted-foreground mt-1">{t.pageSubtitle}</p>
          </div>
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <FinancialDocumentAnalyzer
              onDataExtracted={applyExtractedData}
              onSkip={() => setShowAnalyzer(false)}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t.pageTitle}</h1>
          <p className="text-muted-foreground mt-1">{t.pageSubtitle}</p>
        </div>
        <Building2 className="h-8 w-8 text-primary" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{stepTitles[formData.currentStep - 1]}</CardTitle>
              <CardDescription>{t.stepOf.replace('{current}', formData.currentStep.toString()).replace('{total}', totalSteps.toString())}</CardDescription>
            </div>
            <Progress value={(formData.currentStep / totalSteps) * 100} className="w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
            {stepTitles.map((title, index) => (
              <div key={index} className="flex items-center flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${formData.currentStep > index + 1 ? 'bg-primary text-primary-foreground' : formData.currentStep === index + 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {formData.currentStep > index + 1 ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                </div>
                {index < stepTitles.length - 1 && <div className={`h-0.5 w-4 mx-1 ${formData.currentStep > index + 1 ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            ))}
          </div>

          {formData.currentStep === 1 && <StepGeneral {...stepProps} />}
          {formData.currentStep === 2 && <StepOperations {...stepProps} />}
          {formData.currentStep === 3 && <StepLicenses {...stepProps} />}
          {formData.currentStep === 4 && <StepAssets {...stepProps} />}
          {formData.currentStep === 5 && <StepRealEstate {...stepProps} />}
          {formData.currentStep === 6 && <StepFinancials {...stepProps} />}
          {formData.currentStep === 7 && <StepAddbacks {...stepProps} />}
          {formData.currentStep === 8 && <StepProjections {...stepProps} />}
          {formData.currentStep === 9 && <StepCulture {...stepProps} />}
          {formData.currentStep === 10 && <StepSwot {...stepProps} />}
          {/* Étape 11 (upload de documents) masquée en mode public — voir
              publicDraftToken plus haut : le vendeur envoie ses documents
              directement à son courtier, hors plateforme. */}
          {!isPublicMode && formData.currentStep === 11 && <StepDocuments {...stepProps} uploadedFiles={uploadedFiles} onFileUpload={handleFileUpload} onFileRemove={handleFileRemove} />}

          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button variant="outline" onClick={prevStep} disabled={formData.currentStep === 1}>
              <ChevronLeft className="h-4 w-4 mr-2" /> {t.previous}
            </Button>
            <Button variant="secondary" onClick={saveDraft}>
              <Save className="h-4 w-4 mr-2" /> {t.saveDraft}
            </Button>
            {formData.currentStep < totalSteps ? (
  <Button onClick={nextStep}>
    {t.next} <ChevronRight className="h-4 w-4 ml-2" />
  </Button>
) : isPublicMode ? (
  // AJOUTÉ — bouton de fin en mode public : soumet à
  // dealFormDrafts/{token} au lieu de rediriger vers le checkout
  // (aucun paiement à cette étape, c'est le broker qui gère la suite).
  <Button
    onClick={handleSubmitToBroker}
    disabled={publicSubmitting}
    style={{ background: '#1D314D', color: '#CEAB65', border: '1px solid rgba(206,171,101,0.3)' }}
  >
    {publicSubmitting ? (
      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{language === 'fr' ? 'Envoi...' : 'Sending...'}</>
    ) : (
      <>{language === 'fr' ? 'Soumettre à mon courtier →' : 'Submit to my broker →'}</>
    )}
  </Button>
) : (
  // AJOUTÉ — pour un broker (formData.general.brokerId présent, mode
  // non-public), deux boutons côte à côte : le chemin direct existant
  // (paiement immédiat par le broker) reste inchangé, PLUS le nouveau
  // chemin (b) "Envoyer pour approbation" — le broker choisit lequel
  // utiliser, les deux restent disponibles comme filets mutuels.
  <div className="flex gap-2">
    {formData.general.brokerId && (
      <Button
        variant="outline"
        onClick={handleSendForApproval}
        disabled={sendingForApproval}
        className="gap-1.5"
      >
        {sendingForApproval ? (
          <><Loader2 className="h-4 w-4 animate-spin" />{language === 'fr' ? 'Envoi...' : 'Sending...'}</>
        ) : (
          <>{language === 'fr' ? 'Envoyer au vendeur pour approbation' : 'Send to seller for approval'}</>
        )}
      </Button>
    )}
    <Button
      onClick={() => {
        if (!validateStep(formData.currentStep)) return;
        // AJOUTÉ — brokerFormId inclus, voir handleSubmit() ci-dessus.
        localStorage.setItem('dealSubmissionDraft', JSON.stringify({ formData, uploadedFiles, analyzedDocs, brokerFormId }));
        navigate('/seller/deal-checkout');
      }}
      style={{ background: '#1D314D', color: '#CEAB65', border: '1px solid rgba(206,171,101,0.3)' }}
    >
      <CreditCard className="h-4 w-4 mr-2" />
      {language === 'fr' ? 'Finaliser la soumission →' : 'Finalize Submission →'}
    </Button>
  </div>
)}
          </div>
        </CardContent>
      </Card>

      {/* AJOUTÉ — dialogue de lien après "Envoyer au vendeur pour
          approbation", même besoin que dans BrokerDashboard.tsx : sans
          notification email câblée, c'est l'unique moyen pour le broker
          de récupérer et transmettre le lien lui-même. */}
      <Dialog open={!!approvalLinkUrl} onOpenChange={(open) => !open && setApprovalLinkUrl(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Envoyé pour approbation' : 'Sent for approval'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                                ? "Copiez ce lien et transmettez-le au vendeur pour qu'il puisse réviser et approuver le formulaire."
                : 'Copy this link and send it to the seller so they can review and approve the form.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <Input
              id="approval-link-input"
              readOnly
              value={approvalLinkUrl || ''}
              onFocus={(e) => e.target.select()}
              className="font-mono text-xs"
            />
            <Button onClick={handleCopyApprovalLink} className="shrink-0 gap-1.5">
              {approvalLinkCopied ? (
                <><CheckCircle2 className="h-4 w-4" />{language === 'fr' ? 'Copié !' : 'Copied!'}</>
              ) : (
                <><Copy className="h-4 w-4" />{language === 'fr' ? 'Copier' : 'Copy'}</>
              )}
            </Button>
          </div>
                    <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setApprovalLinkUrl(null)}
            >
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
            <Button
              onClick={() => { setApprovalLinkUrl(null); onComplete?.(); }}
              style={{ background: '#1D314D', color: '#CEAB65' }}
            >
              {language === 'fr' ? 'Terminer et quitter' : 'Finish & Exit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmitDealForm;